/**
 * useCarouselNavigation - Hook for managing carousel navigation and globe panning.
 *
 * Handles the carousel state, navigation callbacks, and syncs globe position
 * with the currently centered trip card.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useGlobeMap } from '@/domain/globe/hooks'
import type { HorizontalScrollCarouselRef } from '@/components/ui/HorizontalScrollCarousel'
import type { TripRow } from './types'

interface UseCarouselNavigationOptions {
  /** Array of trips to navigate */
  trips: TripRow[]
  /** Whether data is still loading */
  loading: boolean
}

interface UseCarouselNavigationReturn {
  /** Ref to attach to HorizontalScrollCarousel */
  carouselRef: React.RefObject<HorizontalScrollCarouselRef | null>
  /** Index of the currently centered card */
  centeredIndex: number
  /** Handler for when the centered card changes */
  handleCenterChange: (index: number) => void
  /** Navigate to previous card */
  handlePrev: () => void
  /** Navigate to next card */
  handleNext: () => void
  /** Navigate to specific card by index */
  handleDotClick: (index: number) => void
}

/**
 * Hook for carousel navigation with globe synchronization.
 *
 * Manages the carousel's centered index state, provides navigation handlers,
 * and automatically pans the globe to match the centered trip's destination.
 *
 * @example
 * ```tsx
 * function TripCarousel({ trips, loading }) {
 *   const {
 *     carouselRef,
 *     centeredIndex,
 *     handleCenterChange,
 *     handlePrev,
 *     handleNext,
 *     handleDotClick,
 *   } = useCarouselNavigation({ trips, loading })
 *
 *   return (
 *     <HorizontalScrollCarousel ref={carouselRef} onCenterChange={handleCenterChange}>
 *       {trips.map(...)}
 *     </HorizontalScrollCarousel>
 *   )
 * }
 * ```
 */
export function useCarouselNavigation({
  trips,
  loading,
}: UseCarouselNavigationOptions): UseCarouselNavigationReturn {
  const [centeredIndex, setCenteredIndex] = useState(0)
  const carouselRef = useRef<HorizontalScrollCarouselRef>(null)
  const { panTo } = useGlobeMap()

  // Total cards in carousel: "New Trip" card at index 0 + trip cards
  const totalCards = trips.length + 1

  // Handle centered card change - pans globe to match
  const handleCenterChange = useCallback(
    (index: number) => {
      setCenteredIndex(index)

      // Index 0 is the "New Trip" card - don't pan globe
      if (index === 0) return

      // Adjust for the "New Trip" card at index 0
      const trip = trips[index - 1]
      if (!trip) return

      const destination = trip.proto.destinations?.[0]

      if (destination?.viewport?.center) {
        panTo(
          destination.viewport.center.latitude,
          destination.viewport.center.longitude,
          { duration: 600, easing: 'ease-in-out' }
        )
      }
    },
    [trips, panTo]
  )

  // Navigate to previous card (wraps around)
  const handlePrev = useCallback(() => {
    const prevIndex = centeredIndex > 0 ? centeredIndex - 1 : totalCards - 1
    carouselRef.current?.scrollToIndex(prevIndex)
  }, [centeredIndex, totalCards])

  // Navigate to next card (wraps around)
  const handleNext = useCallback(() => {
    const nextIndex = centeredIndex < totalCards - 1 ? centeredIndex + 1 : 0
    carouselRef.current?.scrollToIndex(nextIndex)
  }, [centeredIndex, totalCards])

  // Navigate to specific index
  const handleDotClick = useCallback((index: number) => {
    carouselRef.current?.scrollToIndex(index)
  }, [])

  // Initialize globe position when data loads
  useEffect(() => {
    if (trips.length > 0 && !loading) {
      const firstTrip = trips[0]
      const destination = firstTrip.proto.destinations?.[0]

      if (destination?.viewport?.center) {
        // Instant positioning (no animation) on initial load
        panTo(
          destination.viewport.center.latitude,
          destination.viewport.center.longitude,
          { duration: 0 }
        )
      }
    }
  }, [trips.length, loading, panTo])

  return {
    carouselRef,
    centeredIndex,
    handleCenterChange,
    handlePrev,
    handleNext,
    handleDotClick,
  }
}
